import React, { useState, useRef } from 'react';
import { bulkUpdateOdcStatus, cancelOdcOrder } from '../utilities/api';
import { DataTable } from 'primereact/datatable';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FilterMatchMode } from 'primereact/api';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Sidebar } from 'primereact/sidebar';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import * as FileSaver from 'file-saver';

import OdcTableContent from './OdcTableContent';
import OdcView from './OdcView';
import OdcEdit from './OdcEdit';
import '../masters/CategoryMaster.css';

const OdcListContent = ({
    tableData,
    loading,
    isBackgroundSyncing,
    totalRecords,
    filters,
    options,
    userRole,
    fetchFilteredData
}) => {
    const toast = useRef(null);
    const dt = useRef(null);
    const navigate = useNavigate();

    const [globalFilter, setGlobalFilter] = useState({
        value: '',
        filters: { global: { value: null, matchMode: FilterMatchMode.CONTAINS } }
    });
    const [selectedRows, setSelectedRows] = useState([]);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [showSettleModal, setShowSettleModal] = useState(false);
    const [remarks, setRemarks] = useState('');
    const [paymentMode, setPaymentMode] = useState('CASH');
    const [settlementDate, setSettlementDate] = useState(new Date());
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [selectedBillId, setSelectedBillId] = useState(null);
    const [drawerType, setDrawerType] = useState('view');

    const formatters = {
        formatDate: (date) => (date && date !== '0000-00-00' ? moment(date).format('DD-MM-YYYY') : '-'),
        formatAmount: (val) => parseFloat(val || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 }),
        formatValue: (val) => val || '-'
    };

    const selectedTotals = selectedRows.reduce((sum, row) => sum + parseFloat(row.total_paid_amount || 0), 0);
    const labelColSpan = ['pending', 'cancelled'].includes(filters.selectedStatus) ? 4 : 3;

    let _credit = 0, _nonCredit = 0, _grand = 0;
    tableData.forEach((row) => {
        const amt = parseFloat(row.total_paid_amount || 0);
        _grand += amt;
        if (row.payment_type === 'CREDIT' || row.final_mop === 'CREDIT') _credit += amt;
        else _nonCredit += amt;
    });
    const summaries = {
        credit: formatters.formatAmount(_credit),
        nonCredit: formatters.formatAmount(_nonCredit),
        grand: formatters.formatAmount(_grand)
    };

    const handleBulkCancel = async () => {
        try {
            const data = await bulkUpdateOdcStatus({ bill_ids: selectedRows.map((r) => r.bill_id), status: 'cancelled', remarks, updated_by: userRole });
            if (data.status === 'success') {
                toast.current.show({ severity: 'success', summary: 'Cancelled', detail: 'Orders cancelled successfully' });
                setShowCancelModal(false);
                setSelectedRows([]);
                fetchFilteredData();
            }
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Update failed' });
        }
    };

    const handleBulkSettle = async () => {
        try {
            const data = await bulkUpdateOdcStatus({
                bill_ids: selectedRows.map((r) => r.bill_id),
                status: 'settled',
                payment_mode: paymentMode,
                settlement_date: moment(settlementDate).format('YYYY-MM-DD'),
                remarks,
                updated_by: userRole
            });
            if (data.status === 'success') {
                toast.current.show({ severity: 'success', summary: 'Settled', detail: 'Bills settled successfully' });
                setShowSettleModal(false);
                setRemarks('');
                setSelectedRows([]);
                fetchFilteredData();
            }
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Settlement failed' });
        }
    };

    const cancelOrder = async (billId) => {
        try {
            const data = await cancelOdcOrder({ bill_id: billId, updated_by: userRole });
            if (data.status === 'success' || data.code === 200) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: 'Order cancelled successfully', life: 3000 });
                fetchFilteredData();
            } else {
                throw new Error(data.message || 'Failed to cancel');
            }
        } catch (e) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: e.message, life: 3000 });
        }
    };

    const confirmCancel = (rowData) => {
        confirmDialog({
            message: `Are you sure you want to cancel Bill No: ${rowData.bill_no}?`,
            header: 'Cancel Confirmation',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => cancelOrder(rowData.bill_id)
        });
    };

    const exportExcel = async () => {
        const ExcelJSModule = await import('exceljs');
        const ExcelJS = ExcelJSModule.default || ExcelJSModule;
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('ODC Report');

        const status = filters.selectedStatus;
        const companyName = filters.selectedCompany
            ? options.companies.find((c) => String(c.company_id) === String(filters.selectedCompany))?.company_name || 'All'
            : 'All';
        const branchName = filters.selectedBranch
            ? options.branches.find((b) => String(b.branch_id) === String(filters.selectedBranch))?.branch_name || 'All'
            : 'All';
        const dateRange = filters.dates
            ? `${formatters.formatDate(filters.dates[0])} - ${formatters.formatDate(filters.dates[1])}`
            : 'All';

        worksheet.addRow(['ODC Report']);
        worksheet.addRow([`Status: ${status}`]);
        worksheet.addRow([`Company: ${companyName}`]);
        worksheet.addRow([`Branch: ${branchName}`]);
        worksheet.addRow([`Date Range: ${dateRange}`]);
        worksheet.addRow([]);

        let columns = [];
        if (status === 'pending' || status === 'cancelled') {
            columns = [
                { header: 'Order No', key: 'bill_no' },
                { header: 'Order Date', key: 'bill_date' },
                { header: 'Order Time', key: 'bill_time' },
                { header: 'Customer', key: 'customer_name' },
                { header: 'Amount', key: 'total_paid_amount' },
                { header: 'Pay Mode', key: 'pay_mode' },
                { header: 'Status', key: 'status' }
            ];
        } else {
            columns = [
                { header: 'Invoice No', key: 'in_no' },
                { header: 'Invoice Date', key: 'invoice_date' },
                { header: 'Company', key: 'company_name' },
                { header: 'Amount', key: 'total_paid_amount' },
                { header: 'Pay Mode', key: 'pay_mode' }
            ];
            if (status === 'settled') columns.push({ header: 'Settled Date', key: 'stlmnt_date' });
            columns.push({ header: 'Status', key: 'status' });
        }

        const headerRow = worksheet.addRow(columns.map((c) => c.header));
        headerRow.font = { bold: true };
        headerRow.eachCell((cell) => {
            cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            cell.alignment = { horizontal: 'center' };
        });

        const sortedData = [...tableData].sort((a, b) =>
            (a.company_name || '').toLowerCase().localeCompare((b.company_name || '').toLowerCase())
        );

        let totalAmount = 0;
        sortedData.forEach((row) => {
            const amt = parseFloat(row.total_paid_amount || 0);
            totalAmount += amt;
            const rowValues = columns.map((col) => {
                if (['bill_date', 'invoice_date', 'stlmnt_date'].includes(col.key)) return formatters.formatDate(row[col.key]);
                if (col.key === 'total_paid_amount') return amt;
                if (col.key === 'pay_mode') return row.payment_type || row.final_mop;
                return row[col.key];
            });
            const r = worksheet.addRow(rowValues);
            r.eachCell((cell, colNumber) => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
                if (columns[colNumber - 1].header === 'Amount') {
                    cell.numFmt = '#,##0.00';
                    cell.alignment = { horizontal: 'right' };
                }
            });
        });

        const summaryValues = new Array(columns.length).fill('');
        const amtIndex = columns.findIndex((c) => c.header === 'Amount');
        if (amtIndex !== -1) {
            summaryValues[amtIndex - 1] = 'Total:';
            summaryValues[amtIndex] = totalAmount;
        }
        const sumRow = worksheet.addRow(summaryValues);
        sumRow.font = { bold: true };
        if (amtIndex !== -1) {
            const sumCell = sumRow.getCell(amtIndex + 1);
            sumCell.numFmt = '#,##0.00';
            sumCell.alignment = { horizontal: 'right' };
            sumCell.border = { top: { style: 'thin' }, bottom: { style: 'double' } };
        }

        worksheet.columns.forEach((column) => {
            let maxLength = 0;
            column.eachCell({ includeEmpty: true }, (cell) => {
                const len = cell.value ? cell.value.toString().length : 10;
                if (len > maxLength) maxLength = len;
            });
            column.width = maxLength < 12 ? 12 : maxLength + 2;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        FileSaver.saveAs(blob, `ODC_Report_${moment().format('DD_MM_YYYY')}.xlsx`);
    };

    const actionBodyTemplate = (rowData) => (
        <div className="flex gap-2">
            <Button
                icon="pi pi-file-pdf"
                rounded
                text
                className="p-button-info"
                tooltip="View Details"
                tooltipOptions={{ position: 'top' }}
                onClick={() => { setSelectedBillId(rowData.bill_id); setDrawerType('view'); setDrawerVisible(true); }}
            />
            {userRole !== 'MANAGER' && (
                <Button
                    icon="pi pi-pencil"
                    rounded
                    text
                    className="p-button-warning"
                    tooltip="Edit"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => navigate(`/odcedit/${rowData.bill_id}`)}
                />
            )}
            {rowData.status !== 'settled' && (
                <Button
                    icon="pi pi-times-circle"
                    rounded
                    text
                    className="p-button-danger"
                    tooltip="Cancel Order"
                    tooltipOptions={{ position: 'top' }}
                    onClick={() => confirmCancel(rowData)}
                />
            )}
        </div>
    );

    const footerGroup = (
        <ColumnGroup>
            {filters.selectedStatus === 'dispatchedonly' && (
                <>
                    <Row>
                        <Column footer="Payment Pending:" colSpan={labelColSpan} footerStyle={{ textAlign: 'right', color: '#d32f2f', fontWeight: 'bold' }} />
                        <Column footer={summaries.credit} footerStyle={{ textAlign: 'right', fontWeight: 'bold' }} />
                        <Column colSpan={3} />
                    </Row>
                    <Row>
                        <Column footer="Payment Received:" colSpan={labelColSpan} footerStyle={{ textAlign: 'right', color: '#1976d2', fontWeight: 'bold' }} />
                        <Column footer={summaries.nonCredit} footerStyle={{ textAlign: 'right', fontWeight: 'bold' }} />
                        <Column colSpan={3} />
                    </Row>
                </>
            )}
            <Row>
                <Column footer="Total Loaded Sales:" colSpan={labelColSpan} footerStyle={{ textAlign: 'right', fontWeight: 'bold', borderTop: '2px solid #999', backgroundColor: '#f8f9fa' }} />
                <Column footer={summaries.grand} footerStyle={{ textAlign: 'right', fontWeight: 'bold', borderTop: '2px solid #999', color: 'green', backgroundColor: '#f8f9fa' }} />
                <Column colSpan={3} footerStyle={{ borderTop: '2px solid #999', backgroundColor: '#f8f9fa' }} />
            </Row>
        </ColumnGroup>
    );

    return (
        <Card className="bill-reports-container">
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Table header bar */}
            <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-semibold m-0">ODC Orders</h3>
                    <span className="text-sm text-600 ml-1">({tableData.length} records)</span>
                    {isBackgroundSyncing && (
                        <span className="text-primary font-bold flex align-items-center ml-2" style={{ fontSize: '0.8rem' }}>
                            <i className="pi pi-spin pi-spinner mr-1" />
                            Syncing {tableData.length} / {totalRecords || '...'}
                        </span>
                    )}
                    {selectedRows.length > 0 && (
                        <div className="flex gap-2 ml-2">
                            {(filters.selectedStatus === 'pending' || filters.selectedStatus === 'dispatched') && (
                                <Button label="Cancel Selected" icon="pi pi-times" severity="danger" size="small" onClick={() => setShowCancelModal(true)} />
                            )}
                            {filters.selectedStatus === 'dispatched' && (
                                <Button label="Settle Selected" icon="pi pi-check" severity="success" size="small" onClick={() => setShowSettleModal(true)} />
                            )}
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap gap-2 align-items-center">
                    <span className="p-input-icon-left" style={{ minWidth: '220px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilter.value}
                            onChange={(e) =>
                                setGlobalFilter({
                                    value: e.target.value,
                                    filters: { global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } }
                                })
                            }
                            placeholder="Search orders..."
                            className="w-full"
                        />
                    </span>
                    <Button icon="pi pi-file-excel" label="Excel" severity="success" onClick={exportExcel} />
                </div>
            </div>

            {/* DataTable */}
            {loading ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <ProgressSpinner />
                </div>
            ) : (
                <DataTable
                    ref={dt}
                    value={tableData}
                    selection={selectedRows}
                    onSelectionChange={(e) => setSelectedRows(e.value)}
                    dataKey="bill_id"
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    filters={globalFilter.filters}
                    footerColumnGroup={footerGroup}
                    className="p-datatable-sm"
                    showGridlines
                    sortField="company_name"
                    sortOrder={1}
                    stripedRows
                    emptyMessage="No bills found for the selected criteria."
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                    tableStyle={{ minWidth: '50rem' }}
                >
                    <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
                    {OdcTableContent({ status: filters.selectedStatus, actionTemplate: actionBodyTemplate, formatters })}
                </DataTable>
            )}

            {/* Bulk Cancel Modal */}
            <Dialog
                visible={showCancelModal}
                header="Confirm Bulk Cancellation"
                onHide={() => setShowCancelModal(false)}
                footer={
                    <div>
                        <Button label="No" icon="pi pi-times" onClick={() => setShowCancelModal(false)} className="p-button-text" />
                        <Button label="Confirm Cancel" icon="pi pi-check" severity="danger" onClick={handleBulkCancel} />
                    </div>
                }
            >
                <div className="flex flex-column gap-3">
                    <p>Selected Orders: <b>{selectedRows.length}</b></p>
                    <p>Total Value: <b>{formatters.formatAmount(selectedTotals)}</b></p>
                    <InputTextarea placeholder="Enter Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} />
                </div>
            </Dialog>

            {/* Bulk Settle Modal */}
            <Dialog
                visible={showSettleModal}
                header="Confirm Bulk Settlement"
                onHide={() => setShowSettleModal(false)}
                style={{ width: '450px' }}
                footer={
                    <div>
                        <Button label="Cancel" icon="pi pi-times" onClick={() => setShowSettleModal(false)} className="p-button-text" />
                        <Button label="Confirm Settlement" icon="pi pi-check" severity="success" onClick={handleBulkSettle} />
                    </div>
                }
            >
                <div className="flex flex-column gap-3">
                    <div className="p-3 bg-blue-50 border-round">
                        <p className="m-0">Selected Bills: <b>{selectedRows.length}</b></p>
                        <p className="m-0">Total Amount: <b className="text-green-600">{formatters.formatAmount(selectedTotals)}</b></p>
                    </div>
                    <div className="flex flex-column gap-2">
                        <label className="font-bold">Settlement Date</label>
                        <Calendar value={settlementDate} onChange={(e) => setSettlementDate(e.value)} dateFormat="dd-mm-yy" showIcon maxDate={new Date()} />
                    </div>
                    <div className="flex flex-column gap-2">
                        <label className="font-bold">Payment Mode</label>
                        <Dropdown value={paymentMode} options={['CASH', 'UPI', 'CARD', 'CHEQUE', 'NEFT', 'NC']} onChange={(e) => setPaymentMode(e.value)} placeholder="Select Payment Mode" />
                    </div>
                    <div className="flex flex-column gap-2">
                        <label className="font-bold">Remarks</label>
                        <InputTextarea placeholder="Enter settlement remarks..." value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} />
                    </div>
                </div>
            </Dialog>

            {/* View / Edit Sidebar */}
            <Sidebar
                visible={drawerVisible}
                position="right"
                onHide={() => { setDrawerVisible(false); setSelectedBillId(null); }}
                style={{ width: '50vw' }}
                header={drawerType === 'view' ? 'ODC Bill Details' : 'Edit ODC Bill'}
            >
                {drawerVisible && selectedBillId && (
                    drawerType === 'view'
                        ? <OdcView billIdFromProps={selectedBillId} isDrawer={true} />
                        : <OdcEdit billIdFromProps={selectedBillId} isDrawer={true} onSaveSuccess={() => { setDrawerVisible(false); fetchFilteredData(); }} />
                )}
            </Sidebar>
        </Card>
    );
};

export default OdcListContent;
